import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useLocation, Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { Button, TextField, Box, MenuItem, Container, FormControlLabel, Switch } from '@material-ui/core';
import { Freebet, FreebetCampaign, FreebetCampaignPlayer, FreebetCampaignPlayerStat, FreebetCampaignUpdate, FreebetOverviewItem, FreebetType, FreebetUpdate, Player } from "./FreebetModels";
import FreebetUtil from "./FreebetUtil";
import { Title } from "react-admin";
import CreateRoundUtil from "../CreateRound/CreateRoundUtil";
import AdminUtil from "../Admin/AdminUtil";
import { BaseItemState } from "../Ticker2/Ticker2Models";
import { Operator, OperatorSetting } from "../Admin/AdminModels";
import OperatorSelect from "../Components/OperatorSelect";
import DateTimePickerX, { DateTimePickerXValue, getDateTimePickerXValue } from "../CreateFixture/DateTimePickerX";
import FreebetTypeSelect from "./FreebetTypeSelect";
import { GenericResponse } from "../CreateRound/CreateRoundModels";
import { checkIntInput } from "../common/helpers/InputUtils";
import { ItemParams } from "../models/ItemParams";

interface FreebetValue {
    id: number;
    freebetCampaignId: number;
    playerId: number;
    validFrom: Date;
    validTo: Date;
}

interface EditFreebetState {
    item?: Freebet;
    campaign?: FreebetOverviewItem;
    id: number;
    freebetCampaignId: number;
    playerId: string;
    validFrom: DateTimePickerXValue;
    validTo: DateTimePickerXValue;
    valid: boolean;
    freebetCampaignIdError: string;
    playerIdError: string;
    validFromError: string;
    validToError: string;
    value?: FreebetValue;
    update?: FreebetUpdate;
}

interface PlayerRefresh {
    playerId: string;
    response: GenericResponse<FreebetCampaignPlayer>;
}
function checkState(state : EditFreebetState) {
    var valid = true;
    var freebetCampaignId = state.freebetCampaignId;
    var playerId = parseInt(state.playerId);
    var validFrom = state.validFrom.dateTime ? state.validFrom.dateTime : new Date();
    var validTo = state.validTo.dateTime ? state.validTo.dateTime : new Date();
    
    var freebetCampaignErr = "";
    var playerErr = "";
    var validFromErr = "";
    var validToErr = "";
    if (state.freebetCampaignId <= 0) {
        valid = false;
        freebetCampaignErr = "Required";
    }
    if (state.id == 0 || !state.item?.operatorPlayerId) {
        var result = checkIntInput(state.playerId, true, 1, undefined);
        if (!result.valid) {
            valid = false;
            playerErr = result.message;
        }  else if (isNaN(playerId)) {
            valid = false;
            playerErr = "Invalid int";
        }
    }
    if (!state.validFrom.dateTime) {
        valid = false;
        validFromErr = "Required";
    } else if (state.campaign && validFrom < state.campaign.validFrom) {
        valid = false;
        validFromErr = "Less than campaign's ValidFrom";
    } else if (state.campaign && validFrom >= state.campaign.validTo) {
        valid = false;
        validFromErr = "Greater than or equal to campaign's ValidTo";
    }

    if (!state.validTo.dateTime) {
        valid = false;
        validToErr = "Required";
    }
    if (validFromErr.length <=0 && validToErr.length <= 0) {
        if (validTo <= validFrom) {
            valid = false;
            validToErr = "Less than or equal to ValidFrom";
        }
    }

    state.valid = valid;
    state.freebetCampaignIdError = freebetCampaignErr;
    state.playerIdError = playerErr;
    state.validFromError = validFromErr;
    state.validToError = validToErr;

    if (valid) {
        var item = state.item;
        var value : FreebetValue = {
            freebetCampaignId: freebetCampaignId,
            id: item ? item.id : 0,
            playerId: playerId,
            validFrom: validFrom,
            validTo: validTo
        };
        state.value = value;

        var update : FreebetUpdate | undefined = { };
        var changeCount = 0;
        if (item) {
            var dateDiffMsecs = value.validFrom.getTime() - item.validFrom.getTime();
            if (dateDiffMsecs != 0) {
                update.validFrom = value.validFrom;
                changeCount++;
            }
            dateDiffMsecs = value.validTo.getTime() - item.validTo.getTime();
            if (dateDiffMsecs !== 0) {
                update.validTo = value.validTo;
                changeCount++;
            }
            if (changeCount <= 0) {
                update = undefined;
            }
        } else {
            update.freebetCampaignId = value.freebetCampaignId;
            update.playerId = value.playerId;
            update.validFrom = value.validFrom;
            update.validTo = value.validTo;
        }
        state.update = update;
    } else {
        state.value = undefined;
        state.update = undefined;
    }
}
function updateState(state: EditFreebetState, item: Freebet) {
    state.item = item;
    state.id = item.id;
    state.freebetCampaignId = item.freebetCampaignId;
    state.playerId = item.playerId.toString();
    state.validFrom = getDateTimePickerXValue(item.validFrom);
    state.validTo = getDateTimePickerXValue(item.validTo);
    checkState(state);
}
function getDefaultValidFrom() : Date {
    var validFrom = CreateRoundUtil.addHours(new Date(), 1);
    var roundedValidFrom = CreateRoundUtil.getDateWithoutTime(validFrom);
    roundedValidFrom = CreateRoundUtil.addHours(roundedValidFrom, validFrom.getHours());
    return roundedValidFrom;
}
function getDefaultValidTo(validFrom: Date) : Date {
    return CreateRoundUtil.addDays(validFrom, 7);
}
function getInitialState() : EditFreebetState {
    var validFrom = getDefaultValidFrom();
    var validTo = getDefaultValidTo(validFrom);
    var state : EditFreebetState = {
        id: 0,
        freebetCampaignId: 0,
        playerId: "",
        validFrom: getDateTimePickerXValue(validFrom),
        validTo: getDateTimePickerXValue(validTo),
        valid: false,
        freebetCampaignIdError: "",
        playerIdError: "",
        validFromError: "",
        validToError: "",
    };
    checkState(state);
    return state;
}
interface EditFreebetProps {
    freebetId?: number;
    onChange?: (item: Freebet) => void;
    onEdit?: (update?: FreebetUpdate) => void;
    onMessage?: (message?: string) => void;
    saveButtonVisible?: boolean;
}

export type EditFreebetRef = {
	save: () => void;
};
const EditFreebetFR = forwardRef<EditFreebetRef, EditFreebetProps>((props, ref) => {
    const location = useLocation();
    const params: ItemParams  = useParams();
    const history = useHistory();    
   
    const [formState, setFormState] = useState<EditFreebetState>(getInitialState());
    const [playerRefresh, setPlayerRefresh] = useState<PlayerRefresh | undefined>();
    const [saveError, setSaveError] = useState<string>("");
    const [saveStatus, setSaveStatus] = useState<number>(0);
    const [initErr, setInitErr] = useState<string>("");
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [freebetEnabledOperators, setFreebetEnabledOperators] = useState<Map<string, boolean>>(new Map<string, boolean>());

    useEffect(() => {
        initData().then(result => {
        });
    }, [params.id, location.search]);

    // Pass the ref to the useImperativeHandle hook
    useImperativeHandle(ref, () => ({
        save: () => {
            onSave();
        }
    }));

    const compareOperatorSetting = (x: OperatorSetting, y: OperatorSetting) : number => {
        var comp = x.operatorId.localeCompare(y.operatorId);
        if (comp !== 0) {
            return comp;
        }
        comp = x.settingName.localeCompare(y.settingName);
        if (comp !== 0) {
            return comp;
        }
        comp = x.validFrom.getDate() - y.validFrom.getDate();
        if (comp !== 0) {
            return comp;
        }
        return x.id - y.id;
    }
    const getFreebetCampaign = async (freebetCampaignId: number) : Promise<FreebetOverviewItem | undefined> => {
        var freebetOverviewResp = await FreebetUtil.getFreebetOverview(freebetCampaignId);
        if (!freebetOverviewResp.success) {
            setInitErr(`Get FreebetCampaign error [id:${freebetCampaignId}]: ${freebetOverviewResp.message}`);
            return undefined;
        }
        if (!freebetOverviewResp.success) {
            setInitErr(`Get FreebetCampaign error [id:${freebetCampaignId}]: ${freebetOverviewResp.message}`);
            return undefined;
        }
        var campaign = freebetOverviewResp.item;
        if (!campaign) {
            setInitErr(`FreebetCampaign not found [id:${freebetCampaignId}]`);
            return undefined;
        }
        return campaign;
    }
    const checkFreebetCampaign = (campaign: FreebetOverviewItem) : boolean => {
        if (!campaign.enabled) {
            setInitErr("FreebetCampaign is not enabled");
            return false;
        }
        var now = new Date();
        if (campaign.validTo < now) {
            setInitErr("FreebetCampaign valid period has expired.");
            return false;
        }
        if (campaign.currentNumberOfFreebets >= campaign.maxNumberOfFreebets) {
            setInitErr(`FreebetCampaign reached maximum number of freebets [Current:${campaign.currentNumberOfFreebets}] [Max:${campaign.maxNumberOfFreebets}].`)
            return false;
        }
        return true;
    }

    const initData = async () : Promise<number> => {
        var initCount = 0;
        setInitErr("Initializing...");
        setRefreshing(true);
        try {   
            setFormState(getInitialState());
            var id: number = 0;
            if (props.freebetId && props.freebetId > 0) {
                id = props.freebetId;
            } else {
                if (!params.id || params.id.length <= 0 || params.id === "0") {
                    if (!location.search || location.search.length <= 0) {
                        setInitErr("freebetCampaignId param is missing (no params)");
                        return initCount;
                    }
                    let qryParams = new URLSearchParams(location.search);
                    var sCampaignId = qryParams.get("freebetCampaignId");
                    if (!sCampaignId || sCampaignId.length <= 0) {
                        setInitErr("freebetCampaignId param is missing or has no value");
                        return initCount;
                    }
                    var campaignId = parseInt(sCampaignId);
                    if (isNaN(campaignId)) {
                        setInitErr("freebetCampaignId param is not a number");
                        return initCount;
                    }
                    if (campaignId <= 0) {
                        setInitErr("freebetCampaignId param is less than or equal to zero.");
                        return initCount;
                    }
                    var campaign = await getFreebetCampaign(campaignId);
                    if (!campaign) {
                        return initCount;
                    }
                    var fs = {...formState, id: 0};
                    fs.campaign = campaign;
                    fs.freebetCampaignId = campaign.freebetCampaignId;
                    var validFrom = fs.validFrom.dateTime ? fs.validFrom.dateTime : getDefaultValidFrom();
                    if (validFrom < campaign.validFrom) {
                        validFrom = campaign.validFrom;
                    }
                    fs.validFrom = getDateTimePickerXValue(validFrom);
                    if (campaign.defaultValidDays > 0) {
                        fs.validTo = getDateTimePickerXValue(CreateRoundUtil.addDays(validFrom, campaign.defaultValidDays));
                    } else if (fs.validTo.dateTime && fs.validTo.dateTime < campaign.validTo) {
                        fs.validTo.dateTime = campaign.validTo;
                    }
                    setFormState(fs);
                    if (!checkFreebetCampaign(campaign)) {
                        return initCount;
                    }
                    setInitErr("");
                    return initCount;
                }
                id = parseInt(params.id);
                if (isNaN(id) || id <= 0) {
                    setInitErr(`FreebetId param is invalid '${params.id}'.`);
                    return initCount;
                }
            }
            var fs = {...formState, id: id};
            var resp = await FreebetUtil.getFreebet(id);
            if (!resp.success) {
                setInitErr(`Init error: ${resp.message}`);
                return initCount;
            }
            var freebet = resp.item;
            if (freebet) {
                initCount++;
                var campaign = await getFreebetCampaign(freebet.freebetCampaignId);
                fs.campaign = campaign;
                updateState(fs, freebet);
                if (campaign) {
                    setInitErr("") ;
                }
                if (freebet.couponShareId && freebet.couponShareId > 0) {
                    setInitErr(`Freebet has been used and cannot be changed [CouponShareId:${freebet.couponShareId}].`);
                }
            } else {
                setInitErr("Freebet not found.")
                return initCount;
            }
            setFormState(fs);
        } catch (error) {
            console.error("Init error.", error);
            setInitErr(`Init error: ${error}`);
        } finally {
            setRefreshing(false);
        }

        return initCount;
    }
    const handleChange = (name: string, value:any) => {
        var fs = {...formState, [name]: value};
        checkState(fs);
        setFormState(fs);
        if (props.onEdit) {
            props.onEdit(fs.update);
        }
    }
    const onOperatorChange = (item?: Operator) : void => {
        var fs = {...formState, ["operatorId"]: item ? item.id : ""};
        checkState(fs);
        setFormState(fs);
    }
    const onFreebetTypeChange = (item?: FreebetType) : void => {
        var fs = {...formState, ["freebetTypeId"]: item ? item.id : 0};
        checkState(fs);
        setFormState(fs);
    }
    const onValidFromChange = (value: DateTimePickerXValue) : void => {
        var fs = {...formState, ["validFrom"]: value};
        checkState(fs);
        setFormState(fs);
    }
    const onValidToChange = (value: DateTimePickerXValue) : void => {
        var fs = {...formState, ["validTo"]: value};
        checkState(fs);
        setFormState(fs);
    }
    const isFreebetEnabled = (item: Operator) : boolean => {
        var enabled = freebetEnabledOperators.get(item.id);
        if (enabled) {
            return true;
        }
        return false;
    }
    const onSetValidDays = () => {
        if (!formState.campaign || formState.campaign.defaultValidDays <= 0 || formState.validFromError.length > 0) {
            return;
        }
        if (!formState.validFrom.dateTime) {
            return;
        }
        var validTo = CreateRoundUtil.addDays(formState.validFrom.dateTime, formState.campaign.defaultValidDays);
        var fs = {...formState, validTo: getDateTimePickerXValue(validTo)};
        checkState(fs);
        setFormState(fs);
    }
    const onRefreshPlayer = () => {
        const METHOD_NAME = "RefreshPlayer";
        
        var playerId = parseInt(formState.playerId);
        if (isNaN(playerId)) {
            return;
        }
        if (playerId <= 0) {
            setPlayerRefresh({ playerId: formState.playerId, response: CreateRoundUtil.createGenericResponse<FreebetCampaignPlayer>(false, "PlayerId is less than or equal to 0", null)});
            return;
        }
        FreebetUtil.getFreebetCampaignPlayer(formState.freebetCampaignId, playerId).then(resp => {
                setPlayerRefresh({ playerId: formState.playerId, response: resp});
        }).catch(error => {
            setPlayerRefresh({ playerId: formState.playerId, response: CreateRoundUtil.createGenericResponse<FreebetCampaignPlayer>(false, CreateRoundUtil.getExceptionMessage(error, false, "GetPlayer error"), null)});
        });           
    };

    const onSave = () => {
        const METHOD_NAME = "SaveFreebet";
        
        setSaveError("");
        setSaveStatus(0);
        if (!formState.valid) {
            setSaveError("Input is not valid");
            return;
        }
        if (!formState.update) {
            setSaveError("No changes");
            return;
        }
        setSaveStatus(1);
        var saveJson = JSON.stringify(formState.update);
        FreebetUtil.saveFreebet(formState.id, saveJson).then(resp => {
            setSaveStatus(0);
            if (resp.success){
                var item = resp.item;
                if (item) {
                    if (props.onChange) {
                        props.onChange(item);
                    }
                    if (formState.id === 0) {
                        history.push(`/editFreebet/${item.id}`);
                        return;
                    }
                    var fs = {...formState, id: item.id};
                    updateState(fs, item);
                    setFormState(fs);
                    return;
                } 
                setSaveError(`${METHOD_NAME} failure: FreebetCampaign was not returned.`);

            } else {
                setSaveError(resp.message);
            }
        }).catch(error => {
            setSaveError(`${METHOD_NAME} error: ${error}`);
            setSaveStatus(0);
        });
           
    };

    if (props.onMessage) {
        var statusMessage = "";
        if (refreshing) {
            statusMessage = "Refreshing...";
        } else if (saveStatus === 1) {
            statusMessage = "Saving...";
        } else if (initErr && initErr.length > 0) {
            statusMessage = initErr;
        } else if (saveError.length > 0) {
            statusMessage = "Save error";
        }
        props.onMessage(statusMessage);
    }

    var saveEnabled = false;
    var freebet = formState.item;
    var campaign = formState.campaign;
    if (formState.valid && formState.update && !refreshing && saveStatus === 0) {
        saveEnabled = true;
    }
    var initErrorBox = null;
    if (initErr && initErr.length) {
        initErrorBox = (<Box marginTop={3} color="red">{initErr}</Box>);
        saveEnabled = false;
    }

    var saveErrorBox = null;
    if (saveError.length > 0) {
        saveErrorBox = (<Box color="red">{saveError}</Box>);
    }
    var title = freebet ? `Edit Freebet #${freebet.id}` : "Create Freebet";
    var isEdit = freebet ? true : false;

    var freebetCampaignName = formState.freebetCampaignId.toString();
    var freebetCampaignInfo = "";
    var freebetCampaignInfoBox = null;
    if (campaign) {
        freebetCampaignName = `${campaign.freebetCampaignName}`;
        freebetCampaignInfo = `Id: ${campaign.freebetCampaignId} | OperatorId: ${campaign.operatorId} | ExternalId: ${campaign.externalId} | Freebets: ${campaign.currentNumberOfFreebets} of ${campaign.maxNumberOfFreebets}`;
        freebetCampaignInfo += ` | Valid: ${CreateRoundUtil.toGameDateString(campaign.validFrom)} -- ${CreateRoundUtil.toGameDateString(campaign.validTo)} | Enabled: ${campaign.enabled}`;
    } else if (freebet) {
        freebetCampaignName = `${freebet.freebetCampaignName}`;
        freebetCampaignInfo = `Id: ${freebet.freebetCampaignId} | OperatorId: ${freebet.operatorId}`;
    }
    if (freebetCampaignInfo.length > 0) {
        freebetCampaignInfoBox = (<Box fontSize={10}>{freebetCampaignInfo}</Box>);
    }
    var playerName = formState.playerId;
    var playerInfo = null;
    var playerInfoBox = null;
    var playerRefreshId = playerRefresh ? playerRefresh.playerId : "";
    var playerResp = playerRefresh?.response;
    var campaignPlayer : FreebetCampaignPlayer | undefined | null = playerResp?.item;
    var player : Player | undefined | null = campaignPlayer?.player;
    var playerStat : FreebetCampaignPlayerStat | undefined | null = campaignPlayer?.stat;
    var playerErr = formState.playerIdError;
    var refreshPlayerEnabled = false;
    var refreshPlayerBtn = null;
    if (formState.playerId && formState.playerId.length > 0) {
        if (freebet && freebet.playerId.toString() === formState.playerId) {
            playerName = freebet.playerId.toString();
            playerInfo = `OperatorId: ${freebet.operatorId} | ExternalId: ${freebet.operatorPlayerId} | Notes: ${freebet.playerNotes ? freebet.playerNotes : ""}`;
        } else if (player && player.id.toString() === formState.playerId) {
            playerInfo = `OperatorId: ${player.operatorId} | ExternalId: ${player.operatorPlayerId} | Notes: ${player.notes ? player.notes : ""}`;
            if (playerErr.length === 0) {
                if (campaign && campaign.operatorId !== player.operatorId) {
                    playerErr = "Campaign/player operator diff";
                    saveEnabled = false;
                }
            }
            if (playerStat) {
                playerInfo += ` | Freebets [Count:${playerStat.count} | Used:${playerStat.usedCount}]`;
                if (formState.id == 0 && formState.campaign && formState.campaign.maxPlayerFreebets > 0 && playerStat.count >= formState.campaign.maxPlayerFreebets) {
                    const maxPlayerFreebetsReached = `'Max player Freebets' of campaign reached [Max:${formState.campaign.maxPlayerFreebets}]`;
                    if (playerErr && playerErr.length > 0) {
                        playerErr += " | " + maxPlayerFreebetsReached;
                    } else {
                        playerErr = maxPlayerFreebetsReached;
                    }
                    saveEnabled = false;
                }
            }
        } else if (playerResp && playerRefreshId === formState.playerId) {
            if (!playerResp.success) {
                if (playerErr.length === 0) {
                    playerErr = "Player refresh error";
                }
                playerInfo = `Get player response error: ${playerResp.message}`;
            } else if (!player) {
                playerErr = `Player not found`;
            }
            refreshPlayerEnabled = true;
            saveEnabled = false;
        } else {
            if (playerErr.length === 0) {
                playerErr = `Player is not refreshed`;
                refreshPlayerEnabled = true;
            }
            saveEnabled = false;
        }
    } else if (freebet && freebet.externalId && freebet.externalId.length > 0) {
        playerInfo = `OperatorId: ${freebet.operatorId} | ExternalId: ${freebet.operatorPlayerId} | Notes: ${freebet.playerNotes ? freebet.playerNotes : ""}`;
    }
    if (playerInfo) {
        playerInfoBox = (<Box fontSize={10}>{playerInfo}</Box>);
    }
    if (refreshPlayerEnabled) {
        refreshPlayerBtn = (<Button onClick={e => onRefreshPlayer()} color="secondary" variant="outlined" size="small">
                            Refresh
                        </Button>);
    }
    var couponShareInfoBox = null;
    if (isEdit && freebet) {
        var couponShareInfo = `Coupon share info - Id: ${freebet.couponShareId} | ExternalId: ${freebet.externalId}`;
        couponShareInfoBox = (<Box  marginTop={2} fontSize={12}>{couponShareInfo}</Box>);
    }
    var setValidDaysBtn = null;
    if (formState.campaign && formState.campaign.defaultValidDays > 0
        && formState.validFromError.length <= 0 && formState.validFrom.dateTime) {
            setValidDaysBtn = (<Button onClick={e => onSetValidDays()} color="secondary" variant="outlined" size="small">
                            Set valid days ({formState.campaign.defaultValidDays})
                        </Button>);

    }
    var readOnlyProps = { readOnly: true, disableUnderline: true };
    var externalIdBox = null;
    if (formState.id > 0 && formState.item) {
        externalIdBox = (
            <Box marginTop={2}>
                <TextField
                    margin="dense"
                    name="externalId"
                    label="External Id"
                    value={formState.item.externalId}
                    style={{width:400}}
                    InputProps={readOnlyProps}
                    />
            </Box>
        );
    }

    var saveVisible = true;
    if (props.saveButtonVisible === false) {
        saveVisible = false;
    }
    return (
        <Container maxWidth="xl">
            <Title title={title} />
            <Box>
                <TextField
                    label="Id"
                    name="id"
                    value={formState.id}
                    helperText={formState.item ? `CreatedAt: ${CreateRoundUtil.toGameDateString(formState.item.createdAt)} | Status: ${formState.item.status}` : ""}
                    InputProps={readOnlyProps}
                />
            </Box>
            <Box marginTop={2}>
                <TextField
                    label="Freebet Campaign"
                    name="freebetCampaignId"
                    value={freebetCampaignName}
                    style={{width:400}}
                    InputProps={readOnlyProps}
                />
            </Box>
            {freebetCampaignInfoBox}
            {externalIdBox}
            <Box marginTop={2} display="flex" alignItems="center">
                <TextField
                    autoFocus={!isEdit}
                    label="Player Id"
                    name="playerId"
                    value={formState.playerId}
                    onChange={e => handleChange("playerId", e.target.value)}
                    helperText={playerErr}
                    error={playerErr.length > 0}
                    InputProps={isEdit ? readOnlyProps : {}}
                />&nbsp;&nbsp;{refreshPlayerBtn}
            </Box>
            {playerInfoBox}
            <Box marginTop={2} display="flex" alignItems="center">
                <DateTimePickerX 
                    autoFocus={isEdit}
                    label="Valid from"
                    value={formState.validFrom}
                    onChange={onValidFromChange}
                    errorText={formState.validFromError}
                /><span style={{width: 20}} />
                <DateTimePickerX 
                    label="Valid to"
                    value={formState.validTo}
                    onChange={onValidToChange}
                    errorText={formState.validToError}
                /><span style={{width: 20}} />{setValidDaysBtn}
            </Box>
            {couponShareInfoBox}
            {initErrorBox}
            {props.saveButtonVisible === false ? null : (
            <Box marginTop={2}>
                <Button onClick={e => onSave()} color="primary" variant="contained" disabled={!saveEnabled}>
                    Save
                </Button>
            </Box>
            )}
            {saveErrorBox} 
           {props.saveButtonVisible === false ? null : (
            <Box>
             <Button
                component={RouterLink}
                color="secondary"
                size="small"
                style={{textTransform:"none"}}
                to={`/editFreebet?freebetCampaignId=${formState.freebetCampaignId}`}
                disabled={formState.id <= 0}
                >
                Create Freebet
            </Button>&nbsp;|&nbsp;
            <Button
                    component={RouterLink}
                    color="secondary"
                    size="small"
                    style={{textTransform:"none"}}
                    to={"/freebetOverview"}
                    >
                    Goto Freebet Overview
                </Button>
            </Box>
           )}
        </Container>
    );
});

export default EditFreebetFR;
